import { NotificationPublisher } from "dots-ui/dotsNotifications";
import { IDotsMessage } from "dots-ui/dotsToast";
import { FormikErrors, FormikHelpers, FormikProps } from "formik";
import { AvisConstants } from "../data/avisConstants";
import { ResCallback } from "../models";
import { camelize } from "./";

export interface IAxiosError {
  errorMessage: string;
  errorCode?: string;
  status?: number;
  modelState?: Record<string, string[]>;
}

export class ErrorUtils {
  static checkIsAuthorized = (response: any, logout?: () => void): boolean => {
    if (response?.status === 401) {
      if (logout) logout();

      return false;
    }

    return true;
  };

  static getGeneralErrorMessage(
    error?: IAxiosError,
    res?: ResCallback,
  ): string | undefined {
    if (!error) return;

    let result =
      error.errorMessage === ""
        ? this.getGeneralUnknownErrorMessage()
        : error.errorMessage;

    if (error.errorCode && res)
      result += ` ${res("error_ErrorCode", error.errorCode)}`;

    return result;
  }

  static getErrorDotsMessage(
    error: IAxiosError,
    res?: ResCallback,
  ): IDotsMessage {
    return {
      severity: "danger",
      title: "Kļūda",
      content: this.getGeneralErrorMessage(error, res),
    } as IDotsMessage;
  }

  static modelStateToFormikModel(
    obj: Record<string, string[]>,
  ): Record<string, string[]> {
    const transformedObj: Record<string, string[]> = {};

    Object.entries(obj).map(([key]) => {
      const parts = key.split(".");
      let newKey = parts.length === 2 ? camelize(parts[1]) : camelize(parts[0]);
      newKey =
        parts.length === 3
          ? `${camelize(parts[1])}.${camelize(parts[2])}`
          : newKey;
      transformedObj[newKey] = obj[key];
    });

    return transformedObj;
  }

  static handleFormValidation(
    error: IAxiosError,
    formikHelpers: FormikHelpers<any>,
  ) {
    const modelState = error.modelState;

    if (!modelState) return;

    const formikErrors = this.modelStateToFormikModel(modelState);

    Object.entries(formikErrors).forEach(([key, value]) => {
      formikHelpers.setFieldError(key, value[0]);
    });
  }

  static showErrorMessage(
    error?: IAxiosError,
    res?: ResCallback,
    openAsyncTimeout?: number,
  ): void {
    this.handleConditionalAsync(() => {
      const axiosError: IAxiosError = error ?? {
        errorMessage: this.getGeneralUnknownErrorMessage(),
      };

      NotificationPublisher.showToast(
        this.getErrorDotsMessage(axiosError, res),
      );
    }, openAsyncTimeout ?? 0);
  }

  static showSuccessfulMessage(
    title: string,
    msg: string,
    openAsyncTimeout?: number,
  ) {
    //NotificationPublisher.closeAllToasts didn't work before open new toast
    //Workaroud - every notification opens with timeout (0 if not defined)
    NotificationPublisher.closeAllToasts();
    this.handleConditionalAsync(() => {
      NotificationPublisher.showToast({
        severity: "success",
        variant: "filled",
        title: title,
        content: msg,
        details: undefined,
      } as IDotsMessage);
    }, openAsyncTimeout ?? 0);
  }
  static handleConditionalAsync(action: () => void, timeout: number) {
    setTimeout(() => {
      action();
    }, timeout);
  }

  static getGeneralUnknownErrorMessage(res?: ResCallback): string {
    return res
      ? res("error_GeneralUnknownError")
      : AvisConstants.unknown_error_message;
  }

  static getContactLaaErrorMessage(res: ResCallback, errorCode?: string) {
    return errorCode
      ? res("error_ContactAkkaLaaErrorCode", errorCode)
      : res("error_ContactAkkaLaa");
  }

  static getUnknownAxiosError(res: ResCallback): IAxiosError {
    return {
      errorMessage: this.getGeneralUnknownErrorMessage(res),
    };
  }

  static getInternalServerErrorMessage(
    res: ResCallback,
    errorCode?: string,
  ): string {
    return errorCode
      ? res("error_InternalServerErrorCode")
      : res("error_InternalServerError");
  }

  static basicErrorResponseCheck(
    error: any,
    res: ResCallback,
    logout?: () => void,
    showToastErrorMessage = true,
  ): IAxiosError | null {
    // Network error

    if (error.code === "ERR_NETWORK") {
      let resourceNetworkError = res("error_NetworkError");
      //In case If resources are not loaded
      if (!resourceNetworkError || resourceNetworkError === "")
        resourceNetworkError = AvisConstants.unknown_error_message;
      return {
        errorMessage: resourceNetworkError,
      } as IAxiosError;
    }

    const response = error.response;

    if (!response) {
      const result = { errorMessage: this.getGeneralUnknownErrorMessage(res) };

      if (error.status !== 401 && showToastErrorMessage)
        ErrorUtils.showErrorMessage(result, res);

      return result;
    }

    if (response.data?.modelState) {
      return {
        errorMessage: res("error_ValidationError"),
        modelState: response.data?.modelState,
      } as IAxiosError;
    }

    const errorCode = response.data?.errorId;

    // Unauthorized
    if (!this.checkIsAuthorized(response, logout)) {
      return {
        errorMessage: res("error_UnauthorizedError"),
        status: response.status,
      } as IAxiosError;
    }

    // Internal server error
    if (response.status === 500) {
      const result: IAxiosError = {
        errorMessage: this.getInternalServerErrorMessage(res, errorCode),
        errorCode: errorCode,
        status: response.status,
      };

      if (showToastErrorMessage) ErrorUtils.showErrorMessage(result, res);

      return result;
    }

    // Forbidden
    if (response.status === 403) {
      const result: IAxiosError = {
        errorMessage: res("error_Forbidden"),
        status: response.status,
      };

      if (showToastErrorMessage) ErrorUtils.showErrorMessage(result, res);

      return result;
    }

    return null;
  }

  static handleAxiosErrorResponse(
    error: any,
    res: ResCallback,
    logout?: () => void,
  ): IAxiosError | null {
    const errorMsg = this.basicErrorResponseCheck(error, res, logout);

    if (errorMsg) return errorMsg;

    const response = error.response;

    // Bad Response
    if (response.status === 400) {
      const errorCode = response.data?.errorId;

      let msg = this.getGeneralUnknownErrorMessage(res);
      const responseMsg = response.data?.message;

      // todo: clarify whether second check is needed
      if (responseMsg && responseMsg !== res("error_PageNotAvailable"))
        msg = responseMsg;

      const result: IAxiosError = {
        errorMessage: msg,
        status: response.status,
        errorCode: errorCode,
      };

      ErrorUtils.showErrorMessage(result, res);

      return result;
    }

    return null;
  }

  static handleAuthControllerResponse(
    error: any,
    res: ResCallback,
    logout?: () => void,
  ): IAxiosError {
    const errorMsg = this.basicErrorResponseCheck(error, res, logout, false);

    if (errorMsg) return errorMsg;

    const response = error.response;

    // Bad Response
    if (response.status === 400) {
      return {
        errorMessage: res("error_WrongCredentials"),
        status: response.status,
      };
    }

    return this.getUnknownAxiosError(res);
  }

  static getBagErrorText<T extends object>(
    bag: FormikProps<T>,
    bagField: keyof T,
    res: ResCallback,
  ): string | undefined {
    const errorObject = bag.errors[bagField];
    if (!errorObject) return;
    //Specific error message handling
    //If error is plain text return it
    if (typeof errorObject === "string") return errorObject?.toString();

    const textFieldIsRequired = res("validation_FieldIsRequired");

    //If error is not array - default fieldRequired message
    if (!Array.isArray(errorObject)) return textFieldIsRequired;

    const errorArray = errorObject as Array<FormikErrors<T>>;
    let foundFirstError = textFieldIsRequired;

    //Iterate trough FieldArray error object array and seek for first message
    for (const subError of errorArray) {
      if (!subError) continue;
      const foundFirstKey = Object.keys(subError)[0] as keyof T;
      if (!foundFirstKey) continue;
      const errorEntry = subError[foundFirstKey];
      if (!errorEntry) continue;

      foundFirstError = errorEntry.toString();
      break;
    }

    return foundFirstError;
  }
}
